import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NewsArticle } from '../../data/news';
import Header from "../Common/Header/Header";
import useSectionRefs from '../../hooks/useSectionRefs';
import Newsletter from '../Common/Newsletter/Newsletter';
import Footer from '../Common/Footer/Footer';
import ReactMarkdown from 'react-markdown';
import Navbar from '../Common/Navbar/Navbar';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import BackgroundImage from '../../assets/images/exploit.png';
import { fetchArticleBySlugAndCategory, fetchAllArticles } from '../../services/api';
import { Button } from '../../components/ui/button';

const NewsDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const sectionRefs = useSectionRefs();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllSources, setShowAllSources] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  // Get current locale
  const locale = i18n.language || 'fr';
  const navigate = useNavigate();

  const handleOpenContactModal = () => {
    setIsContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setIsContactModalOpen(false);
  };
  
  const { category, slug } = useParams<{ 
    category: string;
    slug: string 
  }>();

  useEffect(() => {
    const fetchData = async () => {
      if (!category || !slug) return;
      
      try {
        setLoading(true);
        // Fetch the specific article
        const articleData = await fetchArticleBySlugAndCategory(category, slug);
        setArticle(articleData);
        
        // Fetch all articles for related content
        const allArticles = await fetchAllArticles();
        setRelatedArticles(allArticles);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Failed to load article. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Reset state when params change
    return () => {
      setArticle(null);
      setLoading(true);
      setError(null);
    };
  }, [category, slug]);

  const categories = [
    { name: 'Exploits', label: 'exploits' as const },
    { name: 'Cyberguerre', label: 'cyberguerre' as const },
    { name: 'Evènements', label: 'event' as const },
    { name: 'Quantique', label: 'quantique' as const }
  ];

  if (loading) {
    return (
      <div className="">
        <Navbar 
          sectionRefs={sectionRefs}
          openContactModal={handleOpenContactModal}
          closeContactModal={handleCloseContactModal}
        />
        <section id="header" className="section dark tw-mb-16" ref={sectionRefs.header}>
          <div className="section dark tw-min-h-[80vh] tw-flex tw-items-center tw-relative">
            <div className="tw-absolute tw-inset-0 tw-z-10">
              <img 
                src={BackgroundImage}
                alt="Fond d'écran cybersécurité" 
                className="tw-w-full tw-h-full tw-object-cover" 
              />
            </div>
            <div className="tw-container tw-mx-auto tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-gap-8 tw-px-4 tw-relative tw-z-10 tw-mt-16 lg:tw-mt-[4em] tw-pt-8">
              <div className="tw-w-full lg:tw-w-3/5 tw-order-1 lg:tw-order-2">
                <div className="tw-relative tw-h-96 tw-rounded-2xl tw-overflow-hidden tw-shadow-xl">
                  <Skeleton className="tw-w-full tw-h-full tw-rounded-2xl" />
                </div>
              </div>
              <div className="tw-w-full lg:tw-w-2/5 tw-space-y-6 tw-order-2 lg:tw-order-1">
                <Skeleton className="tw-h-12 tw-w-full" />
                <Skeleton className="tw-h-6 tw-w-3/4" />
                <Skeleton className="tw-h-6 tw-w-1/2" />
              </div>
            </div>
          </div>
        </section>
        <section className="tw-container tw-mx-auto tw-px-4 tw-py-16">
          <div className="tw-flex tw-flex-row tw-gap-12">
            <div className="tw-w-full lg:tw-w-2/3">
              <Skeleton className="tw-h-8 tw-w-full" />
              <Skeleton className="tw-h-8 tw-w-full tw-mt-4" />
              <Skeleton className="tw-h-8 tw-w-3/4 tw-mt-4" />
              <Skeleton className="tw-h-64 tw-w-full tw-mt-8" />
              <Skeleton className="tw-h-8 tw-w-full tw-mt-4" />
              <Skeleton className="tw-h-8 tw-w-full tw-mt-4" />
            </div>
            <div className="tw-w-1/3 tw-hidden lg:tw-block">
              <Skeleton className="tw-h-64 tw-w-full tw-rounded-lg" />
              <Skeleton className="tw-h-64 tw-w-full tw-mt-8 tw-rounded-lg" />
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-container tw-mx-auto tw-text-center tw-py-20">
        <h2 className="tw-text-2xl tw-font-bold tw-mb-4">Erreur de chargement</h2>
        <p className="tw-text-red-500 tw-mb-6">{error}</p>
        <Button 
          onClick={() => window.location.reload()}
          className="tw-mx-auto"
        >
          Réessayer
        </Button>
        <div className="tw-mt-8">
          <Link to={`/${locale}/cyberguerre`} className="tw-text-blue-500 hover:tw-underline">
            Retour à la liste des articles
          </Link>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="tw-text-center tw-mt-10">
        <h2 className="tw-text-2xl tw-font-bold">Article non trouvé</h2>
        <Link to={`/${locale}/cyberguerre`} className="tw-text-blue-500 hover:tw-underline">
          Retour à la liste des articles
        </Link>
      </div>
    );
  }

  return (
    <div className="">
      {/* Navbar fixe */}
      <Navbar 
        sectionRefs={sectionRefs}
        openContactModal={handleOpenContactModal}
        closeContactModal={handleCloseContactModal}
      />

      {/* Section header sans Navbar */}
      <section id="header" className="section dark tw-mb-16" ref={sectionRefs.header}>
        <div 
          className="section dark tw-min-h-[80vh] tw-flex tw-items-center tw-relative"
        >
          <div className="tw-absolute tw-inset-0 tw-z-10">
            <img 
              src={BackgroundImage}
              alt="Fond d'écran cybersécurité" 
              className="tw-w-full tw-h-full tw-object-cover" 
            />
          </div>
          
          <div className="tw-container tw-mx-auto tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-gap-8 tw-px-4 tw-relative tw-z-10 tw-mt-16 lg:tw-mt-[4em] tw-pt-8">
            {/* Conteneur de l'image - ordre 1 sur mobile, ordre 2 sur desktop */}
            <div className="tw-w-full lg:tw-w-3/5 tw-order-1 lg:tw-order-2">
              <div className="tw-relative tw-h-96 tw-rounded-2xl tw-overflow-hidden tw-shadow-xl">
                <Link 
                  to={`/${locale}/cyberguerre/${article.category}/${article.slug}`} 
                  className="tw-relative tw-h-96 tw-rounded-2xl tw-overflow-hidden tw-shadow-xl tw-block"
                >
                  <img
                    src={article.image}
                    alt={article.title}
                    className="tw-w-full tw-h-full tw-object-cover tw-absolute tw-inset-0 hover:tw-opacity-90 tw-transition-opacity"
                  />
                </Link>
              </div>
            </div>

            {/* Conteneur du titre - ordre 2 sur mobile, ordre 1 sur desktop */}
            <div className="tw-w-full lg:tw-w-2/5 tw-space-y-6 tw-order-2 lg:tw-order-1">
              <h1 className="tw-text-4xl lg:tw-text-5xl tw-font-bold tw-leading-tight tw-text-white">
                {article.title}
              </h1>
              <p className="tw-text-lg lg:tw-text-xl tw-text-white">
                <span className="tw-font-semibold">{new Date(article.date).toLocaleDateString()}</span> - 
                <span className="tw-font-semibold"> Par </span>
                <span className="tw-font-semibold">{article.author}</span>
                <br />
                <span className="tw-font-semibold">Catégorie:</span> <span className="tw-font-semibold">{article.category}</span>
              </p>
            </div>
          </div>
        </div>
      </section>
      <section id="start" className="section light" ref={sectionRefs.start}>
      <div className="section light tw-flex-grow">
        <div className="tw-container tw-mx-auto tw-flex tw-flex-row tw-gap-12 tw-px-4 tw-py-16 lg:tw-pt-24">
          {/* Contenu principal - 2/3 */}
          <div className="tw-w-full lg:tw-w-2/3">
            <ReactMarkdown 
              className="tw-prose tw-max-w-none"
              components={{
                hr: ({node, ...props}) => <hr className="tw-markdown-hr" {...props} />
              }}
            >
              {article.content}
            </ReactMarkdown>
            {/* Section Sources */}
            {article.sources && article.sources.length > 0 && (
              <div className="tw-mt-16 tw-border-t tw-pt-8">
                <h3 className="tw-text-lg tw-font-semibold tw-mb-4">Sources de l'article</h3>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                  {article.sources.slice(0, showAllSources ? undefined : 3).map((source, index) => (
                    <a
                      key={index}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-p-4 tw-bg-gray-50 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors"
                    >
                      <div className="tw-flex tw-items-center tw-gap-[0.45rem]">
                        <span className="tw-text-gray-600">{source.label}</span>
                      </div>
                    </a>
                  ))}
                </div>
                {article.sources.length > 3 && (
                  <button
                    onClick={() => setShowAllSources(!showAllSources)}
                    className="tw-mt-6 tw-text-blue-500 hover:tw-underline tw-flex tw-items-center tw-gap-2"
                  >
                    {showAllSources ? 'Voir -' : 'Voir +'}
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24"
                      className={`tw-w-4 tw-h-4 tw-transition-transform ${showAllSources ? 'tw-rotate-180' : ''}`}
                    >
                      <path fill="currentColor" d="M11 14.2V6q0-.425.288-.712T12 5t.713.288T13 6v8.2l2.9-2.9q.275-.275.7-.275t.7.275t.275.7t-.275.7l-4.6 4.6q-.3.3-.7.3t-.7-.3l-4.6-4.6q-.275-.275-.275-.7t.275-.7t.7-.275t.7.275z"/>
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Bouton de retour centré */}
            <div className="tw-text-left tw-mt-12">
              <Link 
                to={`/${locale}/cyberguerre`}
                className="tw-text-blue-500 hover:tw-underline tw-text-lg tw-border tw-border-blue-500 tw-rounded-lg tw-px-6 tw-py-3 tw-inline-block hover:tw-bg-blue-50 tw-transition-colors"
              >
                ← Retour à la liste des articles
              </Link>
            </div>
          </div>

          {/* Sidebar - 1/3 */}
          <div className="tw-w-1/3 tw-space-y-8 tw-hidden lg:tw-block">
            <Card>
              <CardHeader>
                <CardTitle className="tw-text-lg">Articles les plus lus</CardTitle>
              </CardHeader>
              <CardContent className="tw-space-y-4">
                {relatedArticles.slice(0, 4).map((article) => (
                  <div key={article.id}>
                    <Link 
                      to={`/${locale}/cyberguerre/${article.category}/${article.slug}`}
                      className="tw-text-sm tw-font-medium hover:tw-underline tw-flex tw-items-center tw-gap-[0.45rem]"
                    >
                      <Link 
                        to={`/${locale}/cyberguerre/${article.category}/${article.slug}`}
                        className="tw-relative tw-h-12 tw-w-12 tw-rounded-full tw-overflow-hidden tw-shadow-xl tw-block"
                      >
                        <img
                          src={article.image}
                          alt={article.title}
                          className="tw-w-full tw-h-full tw-object-cover tw-absolute tw-inset-0 hover:tw-opacity-90 tw-transition-opacity"
                        />
                      </Link>
                      {article.title}
                    </Link>
                    <Separator className="tw-my-3" />
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="tw-hidden md:tw-block tw-space-y-8">
              {categories.map((cat) => {
                const latestArticles = relatedArticles
                  .filter(a => a.category === cat.label)
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .slice(0, 3);
                return (
                  <Card key={cat.label} className="tw-bg-gray-50">
                    <CardHeader>
                      <CardTitle className="tw-text-lg">Derniers articles : {cat.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="tw-space-y-4">
                      {latestArticles.map(article => (
                        <div key={article.id} className="tw-flex tw-gap-[0.45rem]">
                          <Link to={`/${locale}/cyberguerre/${article.category}/${article.slug}`}>
                            <img
                              src={article.image}
                              alt={article.title}
                              className="tw-w-16 tw-h-16 tw-object-cover tw-rounded hover:tw-opacity-90 tw-transition-opacity"
                            />
                          </Link>
                          <div>
                            <Link
                              to={`/${locale}/cyberguerre/${article.category}/${article.slug}`}
                              className="tw-text-sm tw-font-medium hover:tw-underline tw-line-clamp-2"
                            >
                              {article.title}
                            </Link>
                            <p className="tw-text-xs tw-text-muted-foreground tw-mt-1">
                              {new Date(article.date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      </section>
      <section id="newsletter" className="section dark" ref={sectionRefs.newsletter}>
        <Newsletter />
        <Footer />
      </section>
    </div>
  );
};

export default NewsDetail;